// No staff available / missed call (route /staffnotavailable), with the optional
// callback-contact form. The record shape written to the call register is identical
// to the old client: { callbackcontact: { email, telephone } }.
import { useEffect, useState } from 'react'
import { QRCodeSVG } from 'qrcode.react'
import BrandLockup from '../components/BrandLockup'
import { useLocation } from 'react-router-dom'
import { updateCallRealTime } from '../core/callStatus'
import { httpRequest } from '../core/http'
import { t } from '../i18n'

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/
const PHONE_RE = /^\+?[0-9 ()-]{6,18}$/

const StaffNotAvailable = () => {
  const { state } = useLocation() as any

  // Explicit per-QR setting deciding whether the contact form is offered (#60),
  // computed by VideoState (with a fallback to the old kiosk/direct-call inference
  // when the scan token predates the backend field). Default to shown if the
  // screen is ever reached without navigation state.
  const showCallbackForm = state?.showCallbackForm !== false

  // CONTINUE ON THE GUEST'S OWN PHONE (#61). A direct-call QR is the keyboard-less
  // public screen, which is exactly where typing is the problem, so that is what
  // gates the code — NOT the form setting above. Those two are complementary: the
  // setting decides whether the on-screen form is offered at all, and this decides
  // whether a guest standing at a screen is additionally given a way to answer from
  // their own phone. On a QR scanned with a phone the guest already has a keyboard,
  // so the code would be pointless there, and offering it when the operator turned
  // the form off on such a QR would also contradict that choice.
  const isPublicScreen = state?.enabledDirectCall == 1
  const showContinueOnPhone = isPublicScreen && !!state?.call_id
  // Absolute, base-aware URL: the code is read by a different device, and this
  // application is served from a deployment base (/caller-beta/ in production)
  // under hash routing.
  const continueOnPhoneUrl = showContinueOnPhone
    ? `${window.location.origin}${import.meta.env.BASE_URL}#/callback/${state.call_id}`
    : ''

  const [wantsContact, setWantsContact] = useState<'' | 'Yes' | 'No'>('')
  const [contactMethod, setContactMethod] = useState<'' | 'email' | 'contact_no'>('')
  const [contactValue, setContactValue] = useState('')
  const [error, setError] = useState('')
  const [isSubmitted, setIsSubmitted] = useState(false)

  useEffect(() => {
    window.history.pushState(null, '', null)
    window.onpopstate = function () {
      window.history.go(1)
    }
    return () => {
      window.onpopstate = null
    }
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const value = contactValue.trim()
    if (contactMethod === 'email' && !EMAIL_RE.test(value)) {
      setError(t.invalidEmail)
      return
    }
    if (contactMethod === 'contact_no' && !PHONE_RE.test(value)) {
      setError(t.invalidPhone)
      return
    }
    setError('')

    const values = {
      email: contactMethod === 'email' ? value : '',
      telephone: contactMethod === 'contact_no' ? value : '',
    }
    // Keep the realtime record for backward compatibility with the old client.
    await updateCallRealTime(state?.call_id, { callbackcontact: values })
    // Route the request to the QR's assigned staff by email (issue #53). Best-effort:
    // the guest gets the confirmation regardless of the notification result.
    if (state?.call_id) {
      httpRequest('callback-request', 'POST', {
        call_id: state.call_id,
        telephone: values.telephone,
        email: values.email,
      })
    }
    setIsSubmitted(true)
  }

  return (
    <div className="cf-page">
      <div className="cf-card">
        <BrandLockup />
        <div className="cf-icon">📵</div>
        <h1 className="cf-title">{t.cannotAssistTitle}</h1>
        <p className="cf-subtitle">{showCallbackForm ? t.callbackIntro : t.callbackKioskMessage}</p>

        {showCallbackForm && wantsContact === '' && (
          <>
            <p className="cf-subtitle">{t.callbackWantContact}</p>
            <div className="cf-radio-group">
              <label className="cf-radio">
                <input type="radio" name="wantsContact" value="Yes" onChange={() => setWantsContact('Yes')} />
                {t.yes}
              </label>
              <label className="cf-radio">
                <input type="radio" name="wantsContact" value="No" onChange={() => setWantsContact('No')} />
                {t.no}
              </label>
            </div>
          </>
        )}

        {wantsContact === 'No' && (
          <p className="cf-subtitle">{t.callbackDeclined}</p>
        )}

        {wantsContact === 'Yes' && !isSubmitted && (
          <>
            <p className="cf-subtitle">{t.callbackPreferredMethod}</p>
            <div className="cf-radio-group">
              <label className={`cf-radio ${contactMethod === 'email' ? 'selected' : ''}`}>
                <input type="radio" name="contactMethod" value="email" onChange={() => setContactMethod('email')} />
                {t.emailLabel}
              </label>
              <label className={`cf-radio ${contactMethod === 'contact_no' ? 'selected' : ''}`}>
                <input type="radio" name="contactMethod" value="contact_no" onChange={() => setContactMethod('contact_no')} />
                {t.phoneLabel}
              </label>
            </div>

            {contactMethod && (
              <form onSubmit={handleSubmit} autoComplete="off" noValidate>
                <div className="cf-field">
                  <label htmlFor="cf-contact">{contactMethod === 'email' ? t.emailLabel : t.phoneLabel}</label>
                  <input
                    id="cf-contact"
                    type={contactMethod === 'email' ? 'email' : 'tel'}
                    inputMode={contactMethod === 'contact_no' ? 'tel' : undefined}
                    value={contactValue}
                    onChange={(e) => setContactValue(e.target.value)}
                  />
                  {error && <span className="cf-error">{error}</span>}
                </div>
                <button type="submit" className="cf-btn cf-btn-primary">
                  {t.send}
                </button>
              </form>
            )}
          </>
        )}

        {isSubmitted && (
          <p className="cf-subtitle">{t.callbackThanks}</p>
        )}

        {/* The code accompanies whatever is above it (message or form) instead of
            replacing it, so a guest who cannot or will not use a phone still reads
            the fallback message. Hidden once details have been left, to avoid
            inviting the same guest to leave them twice. */}
        {showContinueOnPhone && !isSubmitted && (
          <div className="cf-qr">
            <p className="cf-qr-title">{t.callbackScanTitle}</p>
            <p className="cf-subtitle">{t.callbackScanIntro}</p>
            <QRCodeSVG value={continueOnPhoneUrl} size={168} level="M" title={t.callbackScanAlt} />
          </div>
        )}
      </div>
    </div>
  )
}

export default StaffNotAvailable
